/**
 * EarnicoinsBalance.jsx — Primitive UIX (§11/§18) : affichage du solde earnicoins.
 *
 * Source de vérité : User.earnicoinsHoldings (INTEGER) — déjà présent dans Redux
 * `selectors.user` (login + /auth/me renvoient l'objet user complet). Aucun call backend.
 *
 * Un seul composant partagé (§5/§9 DRY) consommé par :
 *   - menu/header.jsx          (dropdown profil — size="sm")
 *   - Settings/ProfileSection  (bloc dédié    — size="lg")
 *   - Profile/PublicProfile    (profil public OWN ONLY — size="md")
 *
 * Icône : logo officiel earnicoins rendu en background-image du <Coin> (immunisé
 * contre les règles <img> du menu). Taille pilotée par SIZES[size].coin.
 *
 * "earnicoins" = nom de devise → conservé tel quel cross-langue (comme USDC/AVAX/EIV, §65).
 */
import React from 'react';
import styled from 'styled-components';
import { COLORS } from './theme';
import earnicoinLogo from '../../assets/images/earnicoin-logo.png';

const SIZES = {
  sm: { coin: '1.1rem', amount: '0.95rem', unit: '0.7rem', gap: '0.45rem' },
  md: { coin: '1.5rem',  amount: '1.25rem', unit: '0.8rem', gap: '0.55rem' },
  lg: { coin: '2.2rem',  amount: '1.9rem',  unit: '0.95rem', gap: '0.7rem' },
};

const Wrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${p => SIZES[p.$size].gap};
  line-height: 1;
`;

const Coin = styled.span`
  display: inline-block;
  width: ${p => SIZES[p.$size].coin};
  height: ${p => SIZES[p.$size].coin};
  flex-shrink: 0;
  /* logo en background-image (immunise contre toute regle <img> du menu :
     ne peut ni deborder ni chevaucher le montant) */
  background: url(${earnicoinLogo}) center / contain no-repeat;
`;

const Amount = styled.span`
  font-family: 'Roboto Mono', monospace; /* §62 — montants en Mono standalone */
  font-weight: 900;
  font-style: italic;                     /* DA V2 — prix/montants italic 900 (§40) */
  font-size: ${p => SIZES[p.$size].amount};
  color: ${COLORS.ink};
`;

const Unit = styled.span`
  font-family: 'Roboto Mono', monospace;
  font-size: ${p => SIZES[p.$size].unit};
  font-weight: 500;
  color: ${COLORS.muted};
  letter-spacing: 0.01em;
`;

/**
 * @param {number} amount   — solde earnicoins (User.earnicoinsHoldings). Null/undefined → 0.
 * @param {'sm'|'md'|'lg'} size — gabarit (défaut 'md').
 * @param {boolean} showUnit — affiche le suffixe "earnicoins" (défaut true).
 */
const EarnicoinsBalance = ({ amount, size = 'md', showUnit = true, className }) => {
  const value = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  return (
    <Wrap $size={size} className={className} title={`${value.toLocaleString('en-US')} earnicoins`}>
      <Coin $size={size} aria-hidden="true" />
      <Amount $size={size}>{value.toLocaleString('en-US')}</Amount>
      {showUnit && <Unit $size={size}>earnicoins</Unit>}
    </Wrap>
  );
};

export default EarnicoinsBalance;
